import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { PlantMark } from "@/app/components/PlantMark";
import { PlantPhotoPicker } from "@/app/components/PlantPhotoPicker";
import { PotIllustration } from "@/app/components/PotIllustration";
import { DeleteConfirmBanner } from "@/app/components/DeleteConfirmBanner";
import { EditScreenHeader } from "@/app/components/EditScreenHeader";
import { usePots } from "../lib/data-context";
import { relDayToISO } from "@/app/lib/dates";

export function PlantEditScreen() {
  const navigate = useNavigate();
  const { id: plantId } = useParams<{ id: string }>();
  const { pots, plants, editPlant, deletePlant } = usePots();
  const plant = plants.find((p) => p.id === plantId);
  const [nickname, setNickname] = useState(plant?.nickname ?? "");
  const [potId, setPotId] = useState(plant?.pot?.id ?? "");
  const [plantedAt, setPlantedAt] = useState(plant?.plantedOn ?? "");
  // Staged photo change: `undefined` means untouched, otherwise the new photo (or null to remove).
  const [photoChange, setPhotoChange] = useState<{ id: string; url: string } | null | undefined>(undefined);
  const todayISO = relDayToISO(0) ?? "";

  if (!plant) return null;

  const inFuture = !!plantedAt && plantedAt > todayISO;
  const dirty =
    nickname.trim() !== (plant.nickname || "") ||
    potId !== (plant.pot.id || "") ||
    plantedAt !== (plant.plantedOn || "") ||
    photoChange !== undefined;

  const save = async () => {
    await editPlant(plant.id, {
      nickname: nickname.trim() || plant.nickname,
      potId: potId || plant.pot.id,
      plantedAt: plantedAt || plant.plantedOn,
      ...(photoChange !== undefined ? { photoImageId: photoChange?.id ?? null } : {}),
    });
    navigate(`/plants/${plant.id}`);
  };

  return (
    <div className="bg-cream min-h-full pb-10">
      <EditScreenHeader
        title="Edit plant"
        saveDisabled={!dirty || inFuture}
        onCancel={() => navigate(`/plants/${plant.id}`)}
        onSave={save}
      />

      <div className="grid place-items-center pt-2 pb-6 bg-cream-2 mx-4.5 mb-4 rounded-[18px] border border-line">
        <PlantPhotoPicker
          photoImageUrl={photoChange === undefined ? plant.photoImageUrl : photoChange?.url ?? null}
          size={168}
          fallback={
            <div
              className="grid place-items-center w-full h-full"
              style={{ background: plant.species?.color ?? "oklch(0.55 0.09 145)" }}
            >
              <PlantMark plant={{ ...plant, photoImageUrl: null }} size={120} />
            </div>
          }
          onChange={(next) => setPhotoChange(next)}
        />
      </div>

      <div className="px-4.5 flex flex-col gap-4">
        <section className="flex flex-col gap-1">
          <h3 className="form-h mb-0">Nickname</h3>
          <input
            className="field-input-bare"
            value={nickname}
            onChange={(e) => setNickname(e.target.value)}
            placeholder="e.g. Mossie"
          />
        </section>

        <section className="flex flex-col gap-1">
          <h3 className="form-h mb-0">Planted on</h3>
          <input
            className="field-input-bare"
            type="date"
            value={plantedAt}
            max={todayISO}
            onChange={(e) => setPlantedAt(e.target.value)}
          />
          {inFuture && (
            <p className="font-sans text-xs text-terra m-0">Planted on can't be in the future.</p>
          )}
        </section>

        <section className="flex flex-col gap-1">
          <h3 className="form-h mb-0">Pot</h3>
          {pots && pots.length > 0 ? (
            <div className="grid grid-cols-2 gap-2">
              {pots.map((pot) => {
                const on = potId === pot.id;
                return (
                  <button
                    type="button"
                    key={pot.id}
                    onClick={() => setPotId(pot.id)}
                    className={`bg-paper border-[1.5px] rounded-[10px] p-2.5 cursor-pointer flex flex-col items-center gap-1 transition-all duration-150 ${on ? "border-moss bg-moss-soft" : "border-line"}`}
                  >
                    <PotIllustration shape={pot.shape} plants={[]} />
                    <span className={`font-sans text-xs font-medium truncate max-w-full text-center ${on ? "text-moss" : "text-ink"}`}>{pot.name}</span>
                  </button>
                );
              })}
            </div>
          ) : (
            <p className="font-sans text-xs text-ink-3 m-0 leading-snug">
              No pots yet — create one from Add → Add a pot.
            </p>
          )}
        </section>

        <DeleteConfirmBanner
          triggerLabel="Delete plant"
          question={`Delete "${plant.nickname}"? This can't be undone.`}
          onConfirm={async () => {
            await deletePlant(plant.id);
            navigate("/");
          }}
        />
      </div>
    </div>
  );
}
